import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import {
    ChevronRight, Clock, ShoppingCart, Package, FileText, AlertCircle,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { quotesApi, QuoteRequest } from '../api/quotes';
import EmailRedirectButton from '../components/EmailRedirectButton';

// ── Status helpers (shared style with ResellerQuotes) ─────────────────────────
const STATUS_COLORS: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border border-amber-200',
    reviewed: 'bg-blue-50 text-blue-700 border border-blue-200',
    accepted: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border border-red-200',
};

const STATUS_LABELS: Record<string, { fr: string; en: string }> = {
    pending: { fr: 'En attente', en: 'Pending' },
    reviewed: { fr: 'En cours de révision', en: 'Under review' },
    accepted: { fr: 'Accepté', en: 'Accepted' },
    rejected: { fr: 'Refusé', en: 'Rejected' },
};

function formatDate(dateStr: string, lang: string) {
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ResellerQuoteDetail() {
    const { lang, setPage, selectedQuoteId, currentUser, addToCart, clearCart } = useApp();
    usePageTitle(lang === 'fr' ? 'Détail du devis — Lodiss' : 'Quote detail — Lodiss');

    const [quote, setQuote] = useState<QuoteRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!selectedQuoteId) {
            setError('not_found');
            setLoading(false);
            return;
        }
        quotesApi.getQuote(selectedQuoteId)
            .then(setQuote)
            .catch(() => setError('not_found'))
            .finally(() => setLoading(false));
    }, [selectedQuoteId]);

    // ── Send to cart (accepted quotes) ──────────────────────────────────────────
    const handleOrderNow = () => {
        if (!quote) return;
        clearCart();
        quote.items.forEach(item => {
            addToCart({
                product: {
                    id: String(item.product_id),
                    category: 'vetements' as any,
                    subcategory: 'vestes' as any,
                    sector: [],
                    name: `Produit ${item.product_id}`,
                    nameEn: `Product ${item.product_id}`,
                    brand: '',
                    ref: '',
                    price: 0,
                    currency: 'TND',
                    stock: 0,
                    moq: 1,
                    quoteThreshold: 0,
                    norm: [],
                    description: '',
                    descriptionEn: '',
                    images: [],
                    variants: {},
                    features: [],
                    featuresEn: [],
                },
                qty: item.quantity,
            });
        });
        setPage('cart');
    };

    // ── Loading ──────────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <main className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-lodiss-red border-t-transparent rounded-full animate-spin" />
            </main>
        );
    }

    // ── Error / 404 ──────────────────────────────────────────────────────────────
    if (error || !quote) {
        return (
            <main className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center p-8 max-w-sm">
                    <AlertCircle size={40} className="mx-auto text-gray-300 mb-4" />
                    <h1 className="text-lg font-bold text-gray-900 mb-2">
                        {lang === 'fr' ? 'Devis introuvable' : 'Quote not found'}
                    </h1>
                    <p className="text-sm text-gray-500 mb-6">
                        {lang === 'fr'
                            ? 'Ce devis n\'existe pas ou vous n\'y avez pas accès.'
                            : 'This quote does not exist or you do not have access.'}
                    </p>
                    <button
                        onClick={() => setPage('reseller-quotes')}
                        className="px-5 py-2.5 bg-lodiss-red text-white text-sm font-semibold rounded-lg hover:bg-lodiss-dark transition-colors"
                    >
                        {lang === 'fr' ? '← Mes devis' : '← My quotes'}
                    </button>
                </div>
            </main>
        );
    }

    const formData = quote.form_data || {};
    const statusColor = STATUS_COLORS[quote.status] ?? 'bg-gray-50 text-gray-700';
    const statusLabel = STATUS_LABELS[quote.status]?.[lang] ?? quote.status;

    return (
        <main className="bg-gray-50 min-h-screen py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* ── Breadcrumb ────────────────────────────────────────────────────── */}
                <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6">
                    <button
                        onClick={() => setPage('reseller-quotes')}
                        className="hover:text-lodiss-red transition-colors font-medium"
                    >
                        {lang === 'fr' ? 'Mes devis' : 'My quotes'}
                    </button>
                    <ChevronRight size={14} className="text-gray-300" />
                    <span className="text-gray-900 font-semibold">
                        {lang === 'fr' ? 'Devis' : 'Quote'} #{quote.id}
                    </span>
                </nav>

                {/* ── Page header ───────────────────────────────────────────────────── */}
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-950">
                            {lang === 'fr' ? 'Devis' : 'Quote'} #{quote.id}
                        </h1>
                        <div className="flex items-center gap-2 mt-1.5 text-sm text-gray-500">
                            <Clock size={14} />
                            <span>{formatDate(quote.created_at, lang)}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Status badge */}
                        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${statusColor}`}>
                            {statusLabel}
                        </span>

                        {/* "Check inbox" smart button */}
                        <EmailRedirectButton
                            userEmail={currentUser?.email ?? (quote.form_data?.email ?? '')}
                            quoteId={quote.id}
                            quoteDate={quote.created_at.slice(0, 10)}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* ── Left column: reseller info + articles ─────────────────────── */}
                    <div className="lg:col-span-2 space-y-5">

                        {/* Reseller info block */}
                        {(formData.name || formData.email || formData.phone || formData.company) && (
                            <div className="bg-white rounded-xl border border-gray-100 p-5">
                                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">
                                    {lang === 'fr' ? 'Informations revendeur' : 'Reseller information'}
                                </h2>
                                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                                    {[
                                        { label: lang === 'fr' ? 'Nom' : 'Name', value: formData.name },
                                        { label: 'Email', value: formData.email },
                                        { label: lang === 'fr' ? 'Téléphone' : 'Phone', value: formData.phone },
                                        { label: lang === 'fr' ? 'Entreprise' : 'Company', value: formData.company },
                                    ].filter(row => row.value).map(row => (
                                        <div key={row.label}>
                                            <dt className="text-xs font-medium text-gray-400 uppercase tracking-wide">{row.label}</dt>
                                            <dd className="text-sm text-gray-900 mt-0.5 font-medium">{row.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}

                        {/* Articles table */}
                        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100">
                                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                                    {lang === 'fr' ? 'Articles demandés' : 'Requested items'}
                                    <span className="ml-2 text-gray-400 font-normal normal-case">
                                        ({quote.items.length})
                                    </span>
                                </h2>
                            </div>

                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                            {lang === 'fr' ? 'Produit' : 'Product'}
                                        </th>
                                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">
                                            {lang === 'fr' ? 'Qté' : 'Qty'}
                                        </th>
                                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                            {lang === 'fr' ? 'Notes' : 'Notes'}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {quote.items.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                        <Package size={16} className="text-gray-400" />
                                                    </div>
                                                    <span className="font-medium text-gray-900">
                                                        #{item.product_id}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3.5 text-center">
                                                <span className="inline-block min-w-[32px] px-2 py-0.5 bg-gray-100 rounded text-gray-700 font-semibold text-xs">
                                                    {item.quantity}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 text-gray-500">
                                                {item.notes || '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Right column: admin notes + action ────────────────────────── */}
                    <div className="space-y-5">

                        {/* Admin notes */}
                        {quote.notes && (
                            <div className="bg-white rounded-xl border border-gray-100 p-5">
                                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <FileText size={14} />
                                    {lang === 'fr' ? 'Notes de l\'équipe Lodiss' : 'Lodiss team notes'}
                                </h2>
                                <div className="bg-gray-50 rounded-lg p-3.5">
                                    <p className="text-sm text-gray-700 leading-relaxed">{quote.notes}</p>
                                </div>
                            </div>
                        )}

                        {/* Contextual action */}
                        <div className="bg-white rounded-xl border border-gray-100 p-5">
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                                {lang === 'fr' ? 'Action' : 'Action'}
                            </h2>

                            {quote.status === 'accepted' ? (
                                <button
                                    onClick={handleOrderNow}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors text-sm"
                                >
                                    <ShoppingCart size={16} />
                                    {lang === 'fr' ? 'Commander maintenant' : 'Order now'}
                                </button>
                            ) : (
                                <p className="text-sm text-gray-500 leading-relaxed">
                                    {lang === 'fr'
                                        ? 'Nous reviendrons vers vous sous 24h ouvrées.'
                                        : 'We will get back to you within 24 business hours.'}
                                </p>
                            )}
                        </div>

                        {/* Quote summary numbers */}
                        <div className="bg-white rounded-xl border border-gray-100 p-5">
                            <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                                {lang === 'fr' ? 'Récapitulatif' : 'Summary'}
                            </h2>
                            <dl className="space-y-2.5 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">{lang === 'fr' ? 'Référence' : 'Reference'}</dt>
                                    <dd className="font-semibold text-gray-900">#{quote.id}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">{lang === 'fr' ? 'Articles' : 'Items'}</dt>
                                    <dd className="font-semibold text-gray-900">{quote.items.length}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">{lang === 'fr' ? 'Statut' : 'Status'}</dt>
                                    <dd>
                                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>
                                            {statusLabel}
                                        </span>
                                    </dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">{lang === 'fr' ? 'Validité' : 'Validity'}</dt>
                                    <dd className="text-gray-900">30 {lang === 'fr' ? 'jours' : 'days'}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}
